import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Typography,
  Box,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import { AttachFile, Close, InfoOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  SubmissionReviewActivity,
  SubmissionType,
  Template,
  TemplateType,
  GroupCategory,
  ParticipantTeam,
} from "../../types";
import { GridList, MappedItem } from "../MapList";
import { GroupCategorySelector } from "../GroupCategory/Selector";
import { SubmissionTypeSelector } from "../SubmissionType/Selector";
import { ManualMappingDialog } from "./ManualMappingDialog";
import { SimpleDebouncedMDXEditor } from "../SimpleDebouncedMDXEditor";
import { FileAttachment } from "../FileUpload/FileAttachment";
import { useMutation } from "@apollo/client";
import {
  GET_ACTIVITY_PDF_UPLOAD_URL,
  CONFIRM_ACTIVITY_PDF_UPLOAD,
  DELETE_ACTIVITY_PDF,
} from "../../Queries";

export interface Props {
  activity: SubmissionReviewActivity;
  templates: Template[];
  categories: GroupCategory[];
  onChange: (activity: SubmissionReviewActivity, label: string) => void;
  onSetGroupCategory: (
    activity: SubmissionReviewActivity,
    groupcategory: GroupCategory | undefined,
  ) => void;
  readOnly?: boolean;
}

/**
 * Preview of the students who will receive the submission request, grouped by
 * team. Shows a "N students across M teams" summary followed by the real member
 * names per team so instructors can verify the roster during setup.
 */
function ParticipantPreview({
  teams,
  flat = false,
}: {
  teams: ParticipantTeam[];
  flat?: boolean;
}) {
  const [t] = useTranslation();

  if (teams.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        {t(
          flat
            ? "No students are enrolled yet."
            : "No participants yet. Assign a group category (or create teams) to preview who will receive the submission request.",
        )}
      </Typography>
    );
  }

  const totalStudents = teams.reduce((sum, team) => sum + team.memberCount, 0);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>
        {flat
          ? `${totalStudents} ${
              totalStudents === 1 ? t("student") : t("students")
            }`
          : `${totalStudents} ${
              totalStudents === 1 ? t("student") : t("students")
            } ${t("across")} ${teams.length} ${
              teams.length === 1 ? t("team") : t("teams")
            }`}
      </Typography>
      <Box
        sx={{
          maxHeight: 440,
          overflowY: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          p: 1.5,
        }}
      >
        {teams.map((team) => (
          <Box key={team.id} sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
            {!flat && (
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {team.name} ({team.memberCount})
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {team.members.map((m) => m.name).join(", ")}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Number input that keeps keystrokes in local state (instant feedback) and
 * commits on blur / Enter. The previous controlled-by-server-state approach
 * meant typing didn't render until a debounced mutation + refetch round-trip,
 * which made the fields feel broken.
 */
function CommittedNumberField({
  value,
  onCommit,
  disabled,
  min,
  max,
  fullWidth,
  sx,
  label,
  helperText,
}: {
  value: number;
  onCommit: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  fullWidth?: boolean;
  sx?: object;
  label?: string;
  helperText?: string;
}) {
  const [local, setLocal] = useState(String(value));
  const focusedRef = React.useRef(false);

  // Track server-state changes, but never clobber in-progress typing
  React.useEffect(() => {
    if (!focusedRef.current) setLocal(String(value));
  }, [value]);

  const commit = () => {
    const parsed = parseInt(local, 10);
    if (!Number.isNaN(parsed) && parsed !== value) {
      onCommit(parsed);
    } else if (Number.isNaN(parsed)) {
      setLocal(String(value)); // revert junk input
    }
  };

  return (
    <TextField
      size="small"
      type="number"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      disabled={disabled}
      inputProps={{ min, max }}
      fullWidth={fullWidth}
      sx={sx}
      label={label}
      helperText={helperText}
    />
  );
}

export function ActivityDetails({
  activity,
  templates,
  categories,
  onChange,
  onSetGroupCategory,
  readOnly = false,
}: Props) {
  const [t] = useTranslation();

  // Date picker open states
  const [openDateOpen, setOpenDateOpen] = useState(false);
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [closeDateOpen, setCloseDateOpen] = useState(false);
  const [reviewOpenDateOpen, setReviewOpenDateOpen] = useState(false);
  const [reviewDueDateOpen, setReviewDueDateOpen] = useState(false);
  const [reviewCloseDateOpen, setReviewCloseDateOpen] = useState(false);
  const [resultsReleaseDateOpen, setResultsReleaseDateOpen] = useState(false);

  // Manual reviewer mapping dialog
  const [mappingOpen, setMappingOpen] = useState(false);

  // File attachment state
  const [attachmentExpanded, setAttachmentExpanded] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);

  // FL-284: dragging a file over the page expands the compact "Add
  // reference document" affordance so the drop zone exists to receive it.
  const attachmentCollapsed =
    !readOnly && !attachmentExpanded && !activity.pdfAttachmentUrl;
  useEffect(() => {
    if (!attachmentCollapsed) return;
    const expand = (event: DragEvent) => {
      if (event.dataTransfer?.types?.includes("Files")) {
        setAttachmentExpanded(true);
      }
    };
    window.addEventListener("dragenter", expand);
    return () => window.removeEventListener("dragenter", expand);
  }, [attachmentCollapsed]);

  const [getUploadUrl] = useMutation(GET_ACTIVITY_PDF_UPLOAD_URL, {
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message);
    },
  });
  const [confirmUpload] = useMutation(CONFIRM_ACTIVITY_PDF_UPLOAD, {
    onCompleted: () => {
      setFileUploading(false);
      setFileUploadError(null);
    },
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message);
    },
  });

  const handleGetUploadUrl = async (filename: string, fileSize: number) => {
    setFileUploading(true);
    setFileUploadError(null);
    const result = await getUploadUrl({
      variables: { activityId: activity.id, filename, fileSize },
    });
    return result.data?.getActivityPdfUploadUrl;
  };

  const handleConfirmUpload = async (
    fileUrl: string,
    originalFilename: string,
  ) => {
    await confirmUpload({
      variables: { activityId: activity.id, fileUrl, originalFilename },
    });
  };

  const [deleteActivityPdf] = useMutation(DELETE_ACTIVITY_PDF, {
    onError: (error) => setFileUploadError(error.message),
  });

  const handleDeleteAttachment = async () => {
    setFileUploadError(null);
    await deleteActivityPdf({ variables: { activityId: activity.id } });
    // Fall back to the compact "Add reference document" affordance.
    setAttachmentExpanded(false);
  };

  const rubrics = templates.filter(
    (f) => f.templateType === TemplateType.Rubric,
  );

  // Live presentations happen in class: no reference document, no submission
  // window, no self-review, and the presenter is visible (so no double-blind).
  const isLive = activity.submissionType === SubmissionType.LivePresentation;
  const isTeamSubmission = activity.submitterMode === "TM";

  // FL-284: the reviewer mapping is editable through Draft / Submissions
  // Open / Submissions Closed; from Reviews Open onward the pairs are
  // materialized as live allocations and the dialog is view-only.
  const mappingEditable = ["DR", "SO", "SC"].includes(activity.status);

  // Sane reviewer defaults follow who-submits: individuals -> random subset,
  // teams -> members of other teams, live presentations -> everyone.
  const defaultReviewerMode = (
    submitterMode: string,
    submissionType: string,
  ) => (submissionType === "LIV" ? "EV" : submitterMode === "TM" ? "RX" : "RD");

  const handleSubmitterModeChange = (submitterMode: string) => {
    onChange(
      {
        ...activity,
        submitterMode,
        reviewerMode: defaultReviewerMode(
          submitterMode,
          activity.submissionType,
        ),
      },
      "Set who submits",
    );
  };

  const handleFormatChange = (submissionType: SubmissionType) => {
    // Switching to a live presentation defaults to team presentations with
    // the whole audience reviewing, identified — the rater is physically
    // present, so anonymity isn't meaningful there.
    const toLive = submissionType === SubmissionType.LivePresentation;
    const submitterMode = toLive ? "TM" : activity.submitterMode;
    onChange(
      {
        ...activity,
        submissionType,
        submitterMode,
        reviewerMode: defaultReviewerMode(submitterMode, submissionType),
        anonymityMode: toLive ? "ID" : activity.anonymityMode,
      },
      "Set submission format",
    );
  };

  const selectedCategory = categories.find(
    (c) => c.id === activity.groupcategoryId,
  );

  const handleDateChange = (
    field: string,
    date: dayjs.Dayjs | null,
    label: string,
  ) => {
    onChange({ ...activity, [field]: date ? date.toISOString() : null }, label);
  };

  const makeDatePicker = (
    label: string,
    field: keyof SubmissionReviewActivity,
    openState: boolean,
    setOpenState: (open: boolean) => void,
  ) => (
    <DesktopDateTimePicker
      open={openState}
      onOpen={() => setOpenState(true)}
      onClose={() => setOpenState(false)}
      label={label}
      value={activity[field] ? dayjs(activity[field] as string) : null}
      onAccept={(date) => handleDateChange(field, date, `Set ${label}`)}
      disabled={readOnly}
      slots={{
        textField: (params) => (
          <TextField
            size="small"
            fullWidth
            {...params}
            onClick={() => !readOnly && setOpenState(true)}
          />
        ),
        clearButton: () => (
          <IconButton
            size="small"
            onClick={() => handleDateChange(field, null, `Clear ${label}`)}
          >
            <Close fontSize="small" />
          </IconButton>
        ),
      }}
      slotProps={{
        actionBar: { actions: ["clear", "accept"] },
      }}
    />
  );

  // ── Left column: Submission settings ────────────────────────────────────

  const submissionItems: MappedItem[] = [
    {
      label: String(t("Submission Format")),
      value: (
        <SubmissionTypeSelector
          selected={
            // Legacy text-only activities render as Written submission
            (activity.submissionType === "TXT"
              ? SubmissionType.Document
              : activity.submissionType) as SubmissionType
          }
          onChange={handleFormatChange}
          disabled={readOnly}
          // Submission review vocabulary: Document/Text are consolidated
          // into a single "Written submission" (inline text or attachment).
          options={[
            { value: SubmissionType.Document, label: "Written submission" },
            { value: SubmissionType.Video, label: "Video" },
            { value: SubmissionType.Link, label: "External link" },
            {
              value: SubmissionType.LivePresentation,
              label: "Live presentation",
            },
          ]}
        />
      ),
    },
    {
      label: String(t("Who Submits")),
      value: (
        <Box>
          <TextField
            select
            size="small"
            fullWidth
            value={activity.submitterMode || "IN"}
            onChange={(e) => handleSubmitterModeChange(e.target.value)}
            disabled={readOnly}
          >
            <MenuItem value="IN">
              {t(isLive ? "Individual presenters" : "Individuals")}
            </MenuItem>
            <MenuItem value="TM">{t(isLive ? "Teams" : "Teams")}</MenuItem>
          </TextField>
          {/* Groupset only matters for holistic team submissions —
              individuals submit across the whole class. */}
          {isTeamSubmission && (
            <Box sx={{ mt: 1 }}>
              <GroupCategorySelector
                categories={categories}
                selected={selectedCategory}
                onChange={(gc) => onSetGroupCategory(activity, gc)}
              />
            </Box>
          )}
          <ParticipantPreview
            teams={activity.participantsPreview ?? []}
            flat={!isTeamSubmission}
          />
        </Box>
      ),
    },
    {
      label: String(t("Description")),
      value: (
        <SimpleDebouncedMDXEditor
          value={activity.description}
          onChange={(description) =>
            onChange({ ...activity, description }, "Update description")
          }
          readOnly={readOnly}
        />
      ),
    },
    // No reference document or submission window for live presentations —
    // the presentation happens in class, there is nothing to hand in.
    ...(isLive
      ? []
      : [
          {
            label: String(t("Attachment")),
            value: (
              <Box>
                {/* Compact by default: a one-line affordance that expands to
                    the full upload control only when needed. */}
                {activity.pdfAttachmentUrl || attachmentExpanded ? (
                  <FileAttachment
                    onGetUploadUrl={handleGetUploadUrl}
                    onConfirmUpload={handleConfirmUpload}
                    onDelete={handleDeleteAttachment}
                    onUploadError={(message) => {
                      setFileUploading(false);
                      setFileUploadError(message);
                    }}
                    disabled={readOnly}
                    acceptedFileTypes=".pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg"
                    maxFileSize={50}
                    uploading={fileUploading}
                    uploadError={fileUploadError}
                    existingFileUrl={activity.pdfAttachmentUrl}
                    autoUpload={true}
                  />
                ) : (
                  <Button
                    size="small"
                    startIcon={<AttachFile fontSize="small" />}
                    onClick={() => setAttachmentExpanded(true)}
                    disabled={readOnly}
                  >
                    {t("Add reference document")}
                  </Button>
                )}
              </Box>
            ),
          },
          {
            label: String(t("Open Date")),
            value: makeDatePicker(
              "Open Date",
              "openDate",
              openDateOpen,
              setOpenDateOpen,
            ),
          },
          {
            label: String(t("Due Date")),
            value: makeDatePicker(
              "Due Date",
              "dueDate",
              dueDateOpen,
              setDueDateOpen,
            ),
          },
          {
            label: String(t("Close Date")),
            value: makeDatePicker(
              "Close Date",
              "closeDate",
              closeDateOpen,
              setCloseDateOpen,
            ),
          },
        ]),
  ];

  // ── Right column: Review settings ──────────────────────────────────────

  const reviewItems: MappedItem[] = [
    {
      label: String(t("Select a Rubric")),
      value: (
        <TextField
          select
          size="small"
          fullWidth
          value={activity.rubricId ?? ""}
          onChange={(e) =>
            onChange(
              { ...activity, rubricId: e.target.value || null },
              "Set rubric",
            )
          }
          disabled={readOnly}
          SelectProps={{ native: true }}
          // FL-284: a rubric is required to open the activity — the old
          // "No rubric (freeform)" choice contradicted that gate. The
          // placeholder is not selectable, like the survey template picker.
          helperText={
            !activity.rubricId
              ? String(t("Select a rubric from your Library to continue."))
              : undefined
          }
        >
          <option value="" disabled>
            {t("Rubric…")}
          </option>
          {rubrics.map((rubric) => (
            <option key={rubric.id} value={rubric.id}>
              {rubric.name}
            </option>
          ))}
        </TextField>
      ),
    },
    {
      label: String(t("Who Reviews")),
      value: (
        <TextField
          select
          size="small"
          fullWidth
          value={activity.reviewerMode || "RD"}
          onChange={(e) =>
            onChange(
              { ...activity, reviewerMode: e.target.value },
              "Set who reviews",
            )
          }
          disabled={readOnly}
          helperText={
            activity.reviewerMode === "EV" || activity.reviewerMode === "MN"
              ? undefined
              : String(
                  t(
                    "Each student reviews one subject; with uneven counts a random student covers two.",
                  ),
                )
          }
        >
          <MenuItem value="RD">{t("Randomized")}</MenuItem>
          <MenuItem value="RX">
            {t("Randomized, avoiding in-team pairings")}
          </MenuItem>
          {/* Rating whole teams needs holistic team submissions */}
          {isTeamSubmission && (
            <MenuItem value="EV">{t("All students rating teams")}</MenuItem>
          )}
          <MenuItem value="MN">{t("Manually selected")}</MenuItem>
        </TextField>
      ),
    },
    ...(activity.reviewerMode === "MN"
      ? [
          {
            label: String(t("Reviewer Mapping")),
            value: (
              <Box>
                {/* FL-284: the mapping stays editable until reviews open
                    (the backend enforces the same boundary), then becomes
                    a read-only view — not blanket-locked with the rest of
                    the config after Draft. */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setMappingOpen(true)}
                >
                  {t(mappingEditable ? "Configure Mapping" : "View Mapping")}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {(activity.manualPairs?.length ?? 0) === 0
                    ? t("No pairings configured yet.")
                    : `${activity.manualPairs?.length} ${t(
                        "pairings configured.",
                      )}`}
                </Typography>
              </Box>
            ),
          },
        ]
      : []),
    {
      label: String(t("Anonymity")),
      labelAdornment: (
        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                {t(
                  "Anonymity only affects what students see — as the instructor you always see who reviewed whom.",
                )}
              </Typography>
              <Typography variant="caption" display="block">
                •{" "}
                {t("Identified: reviewers and authors see each other's names.")}
              </Typography>
              <Typography variant="caption" display="block">
                •{" "}
                {t(
                  "Single-blind: authors don't see who reviewed them; reviewers know whose work they're rating.",
                )}
              </Typography>
              <Typography variant="caption" display="block">
                •{" "}
                {t(
                  "Double-blind: additionally, reviewers don't see whose work they're rating.",
                )}
              </Typography>
            </Box>
          }
        >
          <InfoOutlined
            fontSize="inherit"
            sx={{ ml: 0.5, verticalAlign: "middle", color: "text.secondary" }}
          />
        </Tooltip>
      ),
      value: (
        <TextField
          select
          size="small"
          fullWidth
          // The presenter is visible in a live presentation, so double-blind
          // is impossible — show the coerced single-blind value instead.
          value={
            isLive && (activity.anonymityMode || "SB") === "DB"
              ? "SB"
              : activity.anonymityMode || "SB"
          }
          onChange={(e) =>
            onChange(
              { ...activity, anonymityMode: e.target.value },
              "Set anonymity",
            )
          }
          disabled={readOnly}
          helperText={String(
            t("Controls what students see; you always see all names."),
          )}
        >
          {!isLive && (
            <MenuItem value="DB">
              {t("Double-blind — reviewers and authors hidden from each other")}
            </MenuItem>
          )}
          <MenuItem value="SB">
            {t("Single-blind — authors don't see who reviewed them")}
          </MenuItem>
          <MenuItem value="ID">
            {t("Identified — everyone sees names")}
          </MenuItem>
        </TextField>
      ),
    },
    {
      label: String(t("Min Word Count")),
      value: (
        <CommittedNumberField
          value={activity.minWordCount || 0}
          onCommit={(minWordCount) =>
            onChange({ ...activity, minWordCount }, "Set min word count")
          }
          disabled={readOnly}
          min={0}
          fullWidth
          helperText={String(t("Per text criterion (0 = no minimum)"))}
        />
      ),
    },
    // Self-review does not apply when the "submission" is a live performance.
    ...(isLive
      ? []
      : [
          {
            label: String(t("Self-Review")),
            value: (
              <FormControlLabel
                control={
                  <Switch
                    checked={activity.allowSelfReview}
                    onChange={(e) =>
                      onChange(
                        { ...activity, allowSelfReview: e.target.checked },
                        "Toggle self-review",
                      )
                    }
                    disabled={readOnly}
                    size="small"
                  />
                }
                label=""
              />
            ),
          },
        ]),
    {
      label: String(t("Close the Loop")),
      value: (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={activity.loopStudent}
                onChange={(e) =>
                  onChange(
                    { ...activity, loopStudent: e.target.checked },
                    "Toggle student reflection",
                  )
                }
                disabled={readOnly}
                size="small"
              />
            }
            label={String(t("Student Reflection"))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={activity.loopInstructor}
                onChange={(e) =>
                  onChange(
                    { ...activity, loopInstructor: e.target.checked },
                    "Toggle instructor feedback",
                  )
                }
                disabled={readOnly}
                size="small"
              />
            }
            label={String(t("Instructor Feedback"))}
          />
        </Box>
      ),
    },
    {
      label: String(t("Review Open Date")),
      value: makeDatePicker(
        "Review Open Date",
        "reviewOpenDate",
        reviewOpenDateOpen,
        setReviewOpenDateOpen,
      ),
    },
    {
      label: String(t("Review Due Date")),
      value: makeDatePicker(
        "Review Due Date",
        "reviewDueDate",
        reviewDueDateOpen,
        setReviewDueDateOpen,
      ),
    },
    {
      label: String(t("Review Close Date")),
      value: makeDatePicker(
        "Review Close Date",
        "reviewCloseDate",
        reviewCloseDateOpen,
        setReviewCloseDateOpen,
      ),
    },
  ];

  // ── Bottom row: Grading + Participants ──────────────────────────────────

  const bottomItems: MappedItem[] = [
    {
      label: String(t("Max Points")),
      value: (
        <CommittedNumberField
          value={activity.maxPoints}
          onCommit={(maxPoints) =>
            onChange({ ...activity, maxPoints }, "Set max points")
          }
          disabled={readOnly}
          min={1}
          sx={{ width: 100 }}
        />
      ),
    },
    {
      label: String(t("Grading Weights")),
      value: (
        <Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CommittedNumberField
              label={String(t(isLive ? "Presentation %" : "Submission %"))}
              value={activity.submissionWeight}
              onCommit={(submissionWeight) =>
                onChange(
                  { ...activity, submissionWeight },
                  "Set submission weight",
                )
              }
              disabled={readOnly}
              min={0}
              max={100}
              sx={{ width: 110 }}
            />
            <CommittedNumberField
              label={String(t("Review %"))}
              value={activity.reviewWeight}
              onCommit={(reviewWeight) =>
                onChange({ ...activity, reviewWeight }, "Set review weight")
              }
              disabled={readOnly}
              min={0}
              max={100}
              sx={{ width: 110 }}
            />
            <CommittedNumberField
              label={String(t("Participation %"))}
              value={activity.participationWeight}
              onCommit={(participationWeight) =>
                onChange(
                  { ...activity, participationWeight },
                  "Set participation weight",
                )
              }
              disabled={readOnly}
              min={0}
              max={100}
              sx={{ width: 130 }}
            />
          </Box>
          {/* Component semantics agreed 2026-07-23 — mirror them here so
              faculty don't have to guess what each slice grades. */}
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {t(
                isLive
                  ? "Presentation: credit for presenting (automatic once reviews open)."
                  : "Submission: credit for turning their work in.",
              )}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {t("Review: the average peer rating their work received.")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {t(
                "Participation: credit for completing the reviews assigned to them.",
              )}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      label: String(t("Results Release Date")),
      value: makeDatePicker(
        "Results Release Date",
        "resultsReleaseDate",
        resultsReleaseDateOpen,
        setResultsReleaseDateOpen,
      ),
    },
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Grid container spacing={3}>
          {/* Full-width sections: the app usually runs inside a narrow
              Canvas iframe, so side-by-side columns don't fit. */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t("Submission Settings")}
            </Typography>
            <GridList items={submissionItems} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t("Review Settings")}
            </Typography>
            <GridList items={reviewItems} />
          </Grid>

          {/* Full width: Grading & Participants */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
              {t("Grading")}
            </Typography>
            <GridList items={bottomItems} />
          </Grid>
        </Grid>
      </CardContent>

      {mappingOpen && (
        <ManualMappingDialog
          open={mappingOpen}
          onClose={() => setMappingOpen(false)}
          activity={activity}
          readOnly={!mappingEditable}
        />
      )}
    </Card>
  );
}
